/* The till: a keypad, a queue, and every refusal said in words.
 *
 * ONE COMPONENT, TWO DOORS (2026-09-18). The founder moved the counter into the
 * dashboard: one product, and the role decides what you see. So this is the till
 * with no sign-in of its own. counter.damga.qa renders it as a kiosk (App.tsx), and
 * the dashboard renders the same component as its Counter screen -- for an owner,
 * one screen among the rest; for anyone else on the team, the only one. Both get
 * the same queue, the same enrolment with its email read-back, and the same words
 * for every refusal, because it is the same code.
 *
 * Moved here from App.tsx verbatim; only two things changed hands. "The session
 * went" is now `onSessionEnded`, because signing somebody out is the host's job,
 * and the header's button is `onEnd` with its words, because on a kiosk it ends a
 * shift and in the dashboard it goes back to the shop.
 */
import { Wordmark } from "@damga/ds";
import {
  type HeldEntry,
  type QueueStage,
  Offline,
  SessionExpired,
  createClient,
  createQueue,
  drainQueue,
  isRefusal,
  holdScreenAwake,
  isFull,
  msUntilExpiry,
  queueStage,
  readDeviceId,
  t,
  writeDeviceId,
} from "@damga/kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Keypad } from "./Keypad";
import { Enrol, type EnrolInput } from "./Enrol";
import { Lookup, type Match } from "./Lookup";
import { Outcome, type Result } from "./Outcome";

export type TillBusiness = { id: string; name: string; my_role: string };
type Api = ReturnType<typeof createClient>;

export function formatRemaining(ms: number, lang = "en"): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const unit = (value: number, u: "hour" | "minute") =>
    new Intl.NumberFormat(lang, { style: "unit", unit: u, unitDisplay: "narrow" }).format(value);
  if (h > 0 && m > 0) return `${unit(h, "hour")} ${unit(m, "minute")}`;
  if (h > 0) return unit(h, "hour");
  return unit(m, "minute");
}

export type EarnResponse = {
  accepted?: boolean;
  entry_id?: string;
  member_name?: string | null;
  member_masked?: string | null;
  balance?: number;
  threshold?: number;
  tier_name?: string;
  points_awarded?: number;
  tier_completed?: boolean;
  /** Present when this stamp completed the card: the reward it made. */
  reward_entry_id?: string;
  reward_description?: string;
  replayed?: boolean;
  /** What happened to an address given at enrolment (047). Never a refusal. */
  email?: { kept: boolean; reason: string | null } | null;
};

export function Till({
  api,
  business,
  onSessionEnded,
  endLabel,
  onEnd,
}: {
  api: Api;
  business: TillBusiness | null;
  onSessionEnded: () => void;
  endLabel: string;
  onEnd: () => void;
}) {
  const [result, setResult] = useState<Result | null>(null);
  // GIVING A REWARD (2026-09-18). The keypad takes the customer's code for a
  // reward instead of a stamp. Until this there was no way to hand one over.
  const [giving, setGiving] = useState(false);
  // The code the last stamp was taken with: a stamp that completes the card
  // offers "Give it now", and the customer is still standing there with it.
  const lastCode = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(true);
  const [deviceId, setDeviceId] = useState<string | null>(() => readDeviceId());
  const [held, setHeld] = useState<HeldEntry[]>([]);

  // One store for the life of the app. `createQueue` returns the IndexedDB one
  // where there is an IndexedDB and an in-memory one where there is not —
  // private browsing, mostly. A till on the fallback loses its queue when the
  // tab closes, which is worse than disk and far better than discarding the
  // stamp at the moment it is taken.
  const queue = useMemo(() => createQueue(), []);

  const refreshHeld = useCallback(async () => {
    setHeld(await queue.store.all());
  }, [queue]);

  // THE ESCALATION, RE-READ ON A CLOCK RATHER THAN ONLY ON CHANGE.
  //
  // Pressure rises with time, not just with stamps, so a till that takes one
  // stamp and then sits idle for five hours has to escalate on its own. A
  // minute is fine: the stages are hours apart and the display is a countdown
  // in minutes, so nothing is gained by ticking faster and a busy till would
  // re-render for nothing.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (held.length === 0) return;
    const t = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [held.length]);

  const now = new Date(nowTick);
  const stage: QueueStage = queueStage(held, now);
  const remaining = formatRemaining(msUntilExpiry(held, now) ?? 0);
  const full = isFull(held);


  // Claim this tablet as a till, once, and never again.
  //
  // WITHOUT THIS THE TILL IS ANONYMOUS AND THREE THINGS DO NOT WORK:
  // `last_seen_at` is never written, the device lock that retires a till after
  // ten unknown codes can never fire, and the till-silence alert watches a
  // table with nothing in it. All three read as healthy while doing nothing,
  // which is why this is the queue's first task rather than a detour from it.
  //
  // Silent on purpose. There is no screen and no prompt: naming a tablet is
  // not what anybody wants to do at eight in the morning, and the server picks
  // "Till 3". The merchant renames it in the dashboard.
  //
  // A failure here is not fatal and is not surfaced. Every counter endpoint
  // takes device_id as optional, so an unregistered till still stamps; what is
  // lost is our ability to notice it has gone quiet, which is our problem and
  // not the cashier's. It retries on the next load.
  useEffect(() => {
    if (!business || deviceId) return;
    void (async () => {
      try {
        const res = await api.call<{ device_id?: string }>("/counter/device", {
          method: "POST",
          body: { business_id: business.id },
        });
        if (!isRefusal(res)) {
          const id = (res as { device_id?: string }).device_id ?? null;
          if (id) {
            writeDeviceId(id);
            setDeviceId(id);
          }
        }
      } catch {
        /* Offline, or the session went. Both are handled elsewhere, and a
         * till that cannot register is a till that can still take stamps. */
      }
    })();
  }, [api, business, deviceId]);

  // Send everything held, and delete only what the server answered for. See
  // drainQueue: that rule is the whole of the safety here.
  const drain = useCallback(async () => {
    if (!business) return;
    await drainQueue(
      (body) => api.call<Record<string, unknown>>("/counter/sync", { method: "POST", body }),
      queue.store,
      business.id,
      deviceId,
      () => navigator.onLine,
    );
    await refreshHeld();
  }, [api, business, deviceId, queue, refreshHeld]);

  // THREE TRIGGERS, AND NONE OF THEM IS A TIMER.
  //
  // `online` is the browser telling us the radio came back. `visibilitychange`
  // is the tablet being picked up, which on iOS is the only reliable moment a
  // backgrounded PWA gets to run anything at all — there is no Background Sync
  // there, which is why the six-hour ceiling and the close warning exist.
  // And once when the business loads, because the app may have been reopened
  // hours later with a queue already on disk.
  useEffect(() => {
    if (!business) return;
    void refreshHeld();
    void drain();
    const onOnline = () => void drain();
    const onVisible = () => {
      if (document.visibilityState === "visible") void drain();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [business, drain, refreshHeld]);

  // KEEP THE SCREEN ON WHILE ANYTHING IS HELD.
  //
  // A screen wake lock does not make the queue drain in the background —
  // nothing does, there is no Background Sync on iOS — and it is not a
  // substitute for the close warning. What it buys is the case that actually
  // happens: a tablet propped on a counter, dimming to sleep after two
  // minutes because staff have nothing to do while the connection is down.
  // Awake, the `online` event fires into a live page and the queue drains the
  // moment the router returns. Asleep, it waits for somebody to pick it up.
  //
  // Released as soon as the queue empties, because holding a shop's tablet
  // awake for no reason is a battery cost with nothing behind it.
  useEffect(() => {
    if (held.length === 0) return;
    const lock = holdScreenAwake(() => true);
    return () => lock.release();
  }, [held.length]);

  // The close warning, which the founder called the primary control and is.
  //
  // With no Background Sync on iOS, a till closed with a queue is a queue that
  // never drains: a shop whose connection drops at 20:00 and whose staff close
  // at 22:00 loses everything when the tablet reopens at 08:00. The browser
  // decides the wording of this dialog and we cannot change it; what we can do
  // is make it appear. The counter's own worded version is in the copy table
  // under queue.closing.* for the in-app path.
  useEffect(() => {
    if (held.length === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [held.length]);

  // NO CODE WAITS FOR A NAME ANY MORE. A code that matched two people used to be
  // held here while the cashier searched for the right one. Since 2026-09-17 the
  // server refuses it (E_CODE_AMBIGUOUS) and the counter asks for the next code,
  // which is independent; the lookup below is only the "find customer" screen.
  const [lookup, setLookup] = useState<null | { ambiguous: boolean }>(null);
  const [enrolling, setEnrolling] = useState<null | { phone: string }>(null);

  // ENROL AND STAMP IN ONE CALL, because they are one act at a counter: a
  // customer who is enrolled and not stamped is somebody who gave their number
  // and got nothing, and the cashier has already moved on.
  const enrol = useCallback(
    async (input: EnrolInput) => {
      if (!business) return;
      setBusy(true);
      const idempotencyKey = crypto.randomUUID();
      try {
        const res = await api.call<EarnResponse>("/counter/enrol", {
          method: "POST",
          body: {
            business_id: business.id,
            phone: input.phone,
            display_name: input.name,
            idempotency_key: idempotencyKey,
            // The sentence as the screen rendered it. Sent rather than looked
            // up server-side, because the server does not know which words
            // this cashier saw and a key into a copy table records a sentence
            // this customer may never have been read.
            consent_text: input.consentText,
            consent_locale: "en",
            device_id: deviceId,
            // 047. Only when the cashier read it back and the customer said it
            // was right; Enrol does not call this otherwise.
            ...(input.email
              ? {
                  email: input.email.address,
                  email_consent_text: input.email.consentText,
                  email_consent_locale: "en",
                  email_read_back: true,
                }
              : {}),
          },
        });
        setOnline(true);
        setEnrolling(null);
        setResult(
          isRefusal(res)
            ? { kind: "refused", code: res.refusal, params: res.params }
            : { kind: "stamped", body: res as EarnResponse },
        );
      } catch (err) {
        if (err instanceof SessionExpired) onSessionEnded();
        else if (err instanceof Offline) {
          // NOT QUEUED, and that is a decision rather than an omission.
          //
          // A held stamp carries a code the server resolves later. An
          // enrolment carries a NUMBER and a consent sentence, and queueing it
          // would mean a person and a consent record created hours after the
          // cashier said the words, against a customer who is long gone if
          // anything about it turns out to be wrong. Held earning was already
          // ruled safe because the identity is proven; here it is asserted.
          setOnline(false);
          setResult({ kind: "notSaved" });
        } else setResult({ kind: "error" });
      } finally {
        setBusy(false);
      }
    },
    [api, business, deviceId, onSessionEnded],
  );

  const searchMembers = useCallback(
    async (q: string): Promise<Match[]> => {
      if (!business) return [];
      const res = await api.call<{ results?: Match[] }>("/counter/members/search", {
        method: "POST",
        body: { business_id: business.id, q },
      });
      // A refusal here is not a per-result state -- the only ones this route
      // can produce are authorisation failures, which the shared client has
      // already turned into a thrown SessionExpired. Anything else is an empty
      // list rather than a crash in front of a customer.
      return isRefusal(res) ? [] : (res.results ?? []);
    },
    [api, business],
  );

  const stamp = useCallback(
    async (code: string, chosenUserId?: string) => {
      if (!business) return;
      lastCode.current = code;
      // THE WALL. Not a stage of the escalation: it can arrive at any point on
      // the clock, including with four hours left, and it happens at the
      // counter in front of a customer mid-transaction. Refused here rather
      // than sent, because the server would refuse it too and write an
      // E_OFFLINE_LIMIT row — and a round trip we know the answer to is a
      // round trip that fails slower.
      //
      // Probably the common case rather than the edge: forty was chosen as a
      // fraud bound when nothing was queued, and a busy cafe clears forty
      // transactions in well under six hours.
      if (full) {
        setResult({ kind: "queueFull", count: held.length });
        return;
      }
      setBusy(true);
      // Minted here rather than inside the request, because a stamp that ends
      // up held has to carry the SAME key when it is sent hours later. That is
      // what makes the retry safe: the server has seen this key or it has not.
      const idempotencyKey = crypto.randomUUID();
      const occurredAt = new Date().toISOString();
      try {
        const res = await api.call<EarnResponse>("/counter/earn", {
          method: "POST",
          body: {
            business_id: business.id,
            code,
            idempotency_key: idempotencyKey,
            // Present only when the cashier has just identified the person
            // from the lookup. The server checks it against the candidates
            // THIS code produced, so it narrows an identity rather than
            // asserting one -- without that check it would be a way to stamp
            // anybody by naming them.
            chosen_user_id: chosenUserId ?? null,
            // Null until this tablet has registered. The server treats a
            // missing device as "we do not know which till", which is honest,
            // rather than inventing one.
            device_id: deviceId,
          },
        });
        setOnline(true);
        setLookup(null);
        setResult(
          isRefusal(res)
            ? { kind: "refused", code: res.refusal, params: res.params }
            : { kind: "stamped", body: res as EarnResponse },
        );
      } catch (err) {
        if (err instanceof SessionExpired) {
          onSessionEnded();
        } else if (err instanceof Offline) {
          setOnline(false);
          // HELD NOW, AND THE WORD IS NOT "SAVED".
          //
          // This used to render "Saved. We will confirm this shortly." with
          // nothing behind it: there was no queue, and the stamp was gone. The
          // copy was then changed to say the truth — "Not saved" — and this is
          // the commit where the truth changes back, because the stamp really
          // is on the device now.
          //
          // It still does not say saved. `queue.onDevice` says "This stamp is
          // on this device only. It sends when the connection returns.", which
          // is true to the cashier and to the customer reading the same screen
          // from the other side of it.
          await queue.store.add({
            idempotency_key: idempotencyKey,
            code,
            occurred_at: occurredAt,
          });
          await refreshHeld();
          setResult({ kind: "held" });
        } else {
          setResult({ kind: "error" });
        }
      } finally {
        setBusy(false);
      }
    },
    [api, business, deviceId, full, held.length, queue, refreshHeld, onSessionEnded],
  );

  const undo = useCallback(
    async (entryId: string) => {
      const res = await api.call<{ reversed?: boolean; member_name?: string }>(
        "/counter/reverse",
        { method: "POST", body: { entry_id: entryId } },
      );
      setResult(
        isRefusal(res)
          ? { kind: "refused", code: res.refusal, params: res.params }
          : { kind: "undone", name: (res as { member_name?: string }).member_name ?? "" },
      );
    },
    [api],
  );

  // A reward handed over. Online only: the server takes no offline redemption,
  // so a dropped connection is "not saved", never held -- a queued reward is
  // one that can be given twice.
  const give = useCallback(
    async (code: string, rewardEntryId?: string) => {
      if (!business) return;
      setBusy(true);
      try {
        const res = await api.call<{ claimed?: boolean; reward_description?: string | null }>(
          "/counter/redeem",
          {
            method: "POST",
            body: {
              business_id: business.id,
              member_code: code,
              reward_entry_id: rewardEntryId ?? null,
              device_id: deviceId,
            },
          },
        );
        setOnline(true);
        setResult(
          isRefusal(res)
            ? { kind: "refused", code: res.refusal, params: res.params }
            : { kind: "given", reward: (res as { reward_description?: string | null }).reward_description ?? "" },
        );
      } catch (err) {
        if (err instanceof SessionExpired) onSessionEnded();
        else if (err instanceof Offline) {
          setOnline(false);
          setResult({ kind: "notSaved" });
        } else setResult({ kind: "error" });
      } finally {
        setGiving(false);
        setBusy(false);
      }
    },
    [api, business, deviceId, onSessionEnded],
  );

  return (
    <div className="till">
      {/* Stage drives one attribute and nothing else. Ruling 02: never
          interrupt, at any stage. The only structural change in the whole
          sequence is the header becoming a band in the last half hour, and
          the till keeps taking stamps throughout — a counter that stops
          working to complain about the queue costs the merchant more than the
          queue does. */}
      <header className="till-bar" data-stage={stage}>
        <span className="till-brand">
          <Wordmark size={16} />
        </span>
        <span className="till-where">
          {business ? business.name : t("common.loading")}
          {/* A real count of a real queue. It used to say nothing, correctly,
              because there was no queue and "0 held" would have been a number
              the app invented. */}
          {held.length > 0 && (
            <>
              {" · "}
              {t("queue.waiting", "en", {
                count: String(held.length),
                remaining,
              })}
            </>
          )}
          {!online && held.length === 0 && " · Working offline"}
        </span>
        <button className="linkish" onClick={onEnd}>
          {endLabel}
        </button>
      </header>

      <main className="till-main">
        {result ? (
          <Outcome
            result={result}
            stage={stage}
            count={held.length}
            remaining={remaining}
            onClear={() => setResult(null)}
            onUndo={undo}
            onGive={(rewardEntryId) => {
              if (lastCode.current) void give(lastCode.current, rewardEntryId);
            }}
          />
        ) : enrolling ? (
          <Enrol
            enrol={enrol}
            busy={busy}
            initialPhone={enrolling.phone}
            onBack={() => setEnrolling(null)}
          />
        ) : lookup ? (
          <Lookup
            search={searchMembers}
            online={online}
            busy={busy}
            ambiguous={lookup.ambiguous}
            onPick={(userId) => {
              // The cashier came here from the keypad with no code, so there is
              // nothing to complete: that is the manager override, which has no
              // screen yet. Showing the card was the useful half.
              void userId;
              setLookup(null);
            }}
            onBack={() => {
              setLookup(null);
            }}
            onEnrol={(phone) => {
              setLookup(null);
              setEnrolling({ phone });
            }}
          />
        ) : (
          <div className="till-pad">
            {giving ? (
              <>
                <Keypad
                  onSubmit={(code) => void give(code)}
                  busy={busy}
                  disabled={!business}
                  label={t("counter.giveEnter")}
                />
                <button className="linkish till-find" type="button" onClick={() => setGiving(false)} disabled={busy}>
                  {t("counter.giveBack")}
                </button>
              </>
            ) : (
              <Keypad onSubmit={stamp} busy={busy} disabled={!business} />
            )}
            {/* Reachable BEFORE anything goes wrong, not only after. A cashier
                who has met a flat battery once will look for this. */}
            <button
              className="linkish till-find"
              type="button"
              onClick={() => setLookup({ ambiguous: false })}
              disabled={!business || busy}
            >
              {t("counter.findCustomer")}
            </button>
            {!giving && (
              <button
                className="linkish till-find"
                type="button"
                onClick={() => setGiving(true)}
                disabled={!business || busy}
              >
                {t("counter.giveReward")}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
